import { TABLET_API_ORIGIN } from '@/global/env';
import { RestClientInterface } from '@/lib/restClient';
import { SearchItem } from '@/pages/menu-managements/services/searchItem';
import { MenuManagementBrandItemCreateParams } from '@/services/BrandItem/hooks/create/useMenuManagementCreateBrandItem';
import {
  MenuManagementBrandItemListParams,
  SearchBrandItemListResponse,
} from '@/services/BrandItem/hooks/list/useMenuManagementListBrandItems';
import { SearchCategoryItemsParams } from '@/services/BrandItem/hooks/SearchCategoryItems/useMenuManagementSearchCategoryItems';
import { SearchOptionItemsParams } from '@/services/BrandItem/hooks/SearchOptionItems/useMenuManagementSearchOptionItems';
import { MenuManagementBrandItemUpdateParams } from '@/services/BrandItem/hooks/update/useMenuManagementUpdateBrandItem';
import {
  BulkUpsertBrandCategory,
  BulkUpsertBrandItem,
  BulkUpsertBrandOptionGroup,
} from '@/services/BrandItem/hooks/upload/useMenuManagementBulkUpsertBrandItems';
import { UberPreviewBulkUpsertBrandItemsResponse } from '@/services/BrandItem/hooks/upload/useMenuManagementUberPreviewBulkUpsertBrandItems';
import { menuManagementListQueryParams } from '@/services/Service/MenuManagementList';

import { ShowBrandItemResponse } from './hooks/show/useMenuManagementBrandItemShow';
import { PreviewBulkUpsertBrandItemsResponse } from './hooks/upload/useMenuManagementPreviewBulkUpsertBrandItems';

interface Interface {
  create: (params: MenuManagementBrandItemCreateParams) => Promise<void>;
  searchCategoryItems: (
    params: SearchCategoryItemsParams
  ) => Promise<SearchItem[]>;
  list: (
    params: MenuManagementBrandItemListParams
  ) => Promise<SearchBrandItemListResponse>;
  show: (id: string) => Promise<ShowBrandItemResponse>;
  update: (
    id: string,
    params: MenuManagementBrandItemUpdateParams
  ) => Promise<void>;
  delete: (id: string) => Promise<void>;
  duplicate: (id: string) => Promise<void>;
  previewBulkUpsert: (params: {
    associateBrandId: string;
    file: File;
  }) => Promise<PreviewBulkUpsertBrandItemsResponse>;
  uploadCsvFile: (params: {
    associateBrandId: string;
    file: File;
  }) => Promise<void>;
  previewUberEatsImport: (params: {
    associateBrandId: number;
    masterStoreId: number;
  }) => Promise<UberPreviewBulkUpsertBrandItemsResponse>;
  upsertBrandItems: (params: {
    associateBrandId: number;
    brandItems: BulkUpsertBrandItem[];
    brandCategories: BulkUpsertBrandCategory[];
    brandOptionGroups: BulkUpsertBrandOptionGroup[];
    uberCategoryIdsByUberMenuItemId: {
      [uber_menu_item_id: string]: string[];
    };
    uberModifierGroupIdsByUberMenuItemId: {
      [uber_menu_item_id: string]: string[];
    };
  }) => Promise<void>;
}

export class MenuManagementBrandItemService implements Interface {
  constructor(private readonly restClient: RestClientInterface) {}

  public async create(
    params: MenuManagementBrandItemCreateParams
  ): Promise<void> {
    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items` as const,
      params
    );
  }

  public async searchCategoryItems(
    params: SearchCategoryItemsParams
  ): Promise<SearchItem[]> {
    const query =
      `?associate_brand_id=${params.associate_brand_id.toString()}&title=${
        params.title
      }` as const;

    return await this.restClient.get(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/search/category-items${query}` as const
    );
  }
  // dashboard-api/menu-delivery/items/search
  public async searchOptionGroupItems(
    params: SearchOptionItemsParams
  ): Promise<SearchItem[]> {
    const query =
      `?associate_brand_id=${params.associate_brand_id.toString()}&title=${
        params.title
      }` as const;

    return await this.restClient.get(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/search/option-group-items${query}` as const
    );
  }

  public async list(
    params: MenuManagementBrandItemListParams
  ): Promise<SearchBrandItemListResponse> {
    const query = menuManagementListQueryParams(params);
    return await this.restClient.get<SearchBrandItemListResponse>(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items${query}` as const
    );
  }

  public async show(id: string): Promise<ShowBrandItemResponse> {
    return await this.restClient.get(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/${id}` as const
    );
  }

  public async update(
    id: string,
    params: MenuManagementBrandItemUpdateParams
  ): Promise<void> {
    return await this.restClient.patch(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/${id}` as const,
      params
    );
  }

  public async delete(id: string): Promise<void> {
    return await this.restClient.delete(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/${id}` as const
    );
  }
  public async duplicate(id: string): Promise<void> {
    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/${id}/duplicate` as const
    );
  }
  public async previewBulkUpsert(params: {
    associateBrandId: string;
    file: File;
  }): Promise<PreviewBulkUpsertBrandItemsResponse> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('associate_brand_id', params.associateBrandId);

    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/preview-upload` as const,
      form
    );
  }
  public async uploadCsvFile(params: {
    associateBrandId: string;
    file: File;
  }): Promise<void> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('associate_brand_id', params.associateBrandId);

    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/upload` as const,
      form
    );
  }

  public async previewUberEatsImport(params: {
    associateBrandId: number;
    masterStoreId: number;
  }): Promise<UberPreviewBulkUpsertBrandItemsResponse> {
    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/preview-uber-eats-menu-import` as const,
      {
        associate_brand_id: params.associateBrandId,
        master_store_id: params.masterStoreId,
      }
    );
  }

  public async upsertBrandItems(params: {
    associateBrandId: number;
    brandItems: BulkUpsertBrandItem[];
    brandCategories: BulkUpsertBrandCategory[];
    brandOptionGroups: BulkUpsertBrandOptionGroup[];
    uberCategoryIdsByUberMenuItemId: {
      [uber_menu_item_id: string]: string[];
    };
    uberModifierGroupIdsByUberMenuItemId: {
      [uber_menu_item_id: string]: string[];
    };
  }): Promise<void> {
    return await this.restClient.post(
      `${TABLET_API_ORIGIN}/dashboard-api/brand-items/bulk-upsert` as const,
      {
        associate_brand_id: params.associateBrandId,
        brand_items: params.brandItems,
        brand_categories: params.brandCategories,
        brand_option_groups: params.brandOptionGroups,
        uber_category_ids_by_uber_menu_item_id:
          params.uberCategoryIdsByUberMenuItemId,
        uber_modifier_group_ids_by_uber_menu_item_id:
          params.uberModifierGroupIdsByUberMenuItemId,
      }
    );
  }
}
